import { useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/useCausesDashboardAnalytics'
import { useAuthState } from '@percent/cause-dashboard/common/hooks'
import debounce from 'lodash/debounce'
import {
  ProgramGenericEventKind,
  MarketplaceProgrammeVisitedSource,
  ProgrammeViewEventKind,
  MarketingMetadataKey,
  MarketingMetadataKeyStore,
  ProgramTracking,
  OriginCategory
} from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/marketplaceAnalytics.types'

type ExperimentMetadata = {
  experiment?: {
    name: string
    variant: boolean
  }
}

interface ProgramVisitedTrackingProps {
  source: MarketplaceProgrammeVisitedSource
  targetUrl: string
  programmeOfferName: string
  programTracking?: ProgramTracking
  productCategory?: string
  metadata?: Record<MarketingMetadataKeyStore, string | boolean | number> & ExperimentMetadata
  originCategory?: OriginCategory
  isNativeApply?: boolean
}

interface ProgramViewEventProps {
  eventKind: ProgrammeViewEventKind
  programmeName?: string
  page: string
}

interface GenericMarketplaceEventProps {
  eventKind: ProgramGenericEventKind
  programmeName: string
  source: string
  metadata?: Record<MarketingMetadataKey, string | boolean | number>
}

interface MarketplaceSearchProps {
  searchQuery: string
  experiment?: {
    name: string
    variant: boolean
  }
}

export const useMarketplaceAnalytics = () => {
  const { track } = useCausesPortalAnalytics()
  const { authState } = useAuthState()
  const { organisation, user } = authState

  const trackProgramVisited = debounce(
    (props: ProgramVisitedTrackingProps) =>
      track('Program Visited', {
        country: organisation?.countryCode,
        organisation: organisation?.displayName,
        organisationId: organisation?.id,
        programmeName: props.programmeOfferName,
        programTracking: props.programTracking,
        clickedFrom: props.source,
        targetUrl: props.targetUrl,
        isNativeApply: props.isNativeApply,
        productCategory: props.productCategory,
        metadata: props.metadata,
        originCategory: props.originCategory,
        accountId: user?.id
      }),
    1000
  )

  const trackProgramViewEvent = debounce(
    ({ eventKind, programmeName, page }: ProgramViewEventProps) =>
      track(eventKind, {
        programmeName,
        page,
        accountId: user?.id
      }),
    1000
  )

  const trackProgramEvent = debounce(
    ({ eventKind, programmeName, source, metadata }: GenericMarketplaceEventProps) =>
      track(eventKind, {
        programmeName,
        source,
        metadata,
        accountId: user?.id
      }),
    1000
  )

  const trackMarketplaceSearch = debounce(
    ({ searchQuery, experiment }: MarketplaceSearchProps) =>
      track('Marketplace Search', {
        searchQuery,
        experiment,
        accountId: user?.id
      }),
    1000
  )

  const trackMarketplaceSearchPillClick = debounce(
    ({ searchQuery }: MarketplaceSearchProps) =>
      track('Marketplace Search Pill Click', {
        searchQuery,
        accountId: user?.id
      }),
    1000
  )

  return {
    trackProgramVisited,
    trackProgramViewEvent,
    trackProgramEvent,
    trackMarketplaceSearch,
    trackMarketplaceSearchPillClick
  }
}
